import axios, { AxiosResponse } from "axios";

import {
  ConfigResponseType,
  LoginPayloadType,
  ReportDataResponseType,
  SiteTextsResponseType,
  SubmitReportResponseType,
  TokenCheckResponseType,
} from "./monitorApiTypes";
import { authConfig, client, webConfig } from "./monitorClient";

const monitorApi = {
  getSiteTexts: (): Promise<AxiosResponse<SiteTextsResponseType>> =>
    client.get("/site-texts/"),

  getConfig: (): Promise<AxiosResponse<ConfigResponseType>> =>
    client.get("/config/"),

  getDnsMonitoringData: async (
    sessionId: string | null = null,
  ): Promise<any> => {
    const params = sessionId != null ? { session_id: sessionId } : {};
    const res = await client.get("/dns_monitor_api", {
      params,
      timeout: 10_000,
    });
    return res.data;
  },

  checkDnsByDomain: async (domainName: string): Promise<any> => {
    const res = await client.post(
      "/dns_monitor_api",
      { domain_name: domainName },
      { timeout: 240_000 },
    );
    return res.data;
  },

  checkToken: (): Promise<AxiosResponse<TokenCheckResponseType>> =>
    client.post("/check/", {}, authConfig()),

  getStats: (params: Record<string, any> = {}): Promise<AxiosResponse<any>> =>
    client.get("/stat/", { params }),

  getAllStats: (): Promise<AxiosResponse<any>> => client.get("/stat_all/"),

  getLatestShutdownMapReport: (): Promise<AxiosResponse<any>> =>
    client.get("/shutdown_map_report?n=1"),

  publishShutdownMapReport: (payload: any): Promise<AxiosResponse<any>> =>
    client.post("/shutdown_map_report", payload, authConfig()),

  getShutdownTable: (date: string): Promise<AxiosResponse<any>> =>
    client.get(`/get_shutdown_table?date=${date}`, authConfig()),

  addShutdownTable: (data: any): Promise<AxiosResponse<any>> =>
    client.post("/add_shutdown_table", data, authConfig()),

  uploadShutdownXlsx: (formData: FormData): Promise<AxiosResponse<any>> =>
    client.post("/table_upload", formData, authConfig()),

  downloadShutdownXlsx: (
    from: string,
    to: string,
  ): Promise<AxiosResponse<Blob>> =>
    client.get(`/table_download?from=${from}&to=${to}`, {
      ...authConfig(),
      responseType: "blob",
    }),

  login: (payload: LoginPayloadType): Promise<AxiosResponse<any>> =>
    client.post("/login9M3F5F", payload),

  uploadDnsCheckCsv: (formData: FormData): Promise<AxiosResponse<any>> =>
    client.post("/upload_dns_check_csv", formData, webConfig()),

  getWhiteListPrecalc: (): Promise<AxiosResponse<any>> =>
    client.get("/white_list_checker_precalc", {
      ...authConfig(),
      params: { t: Date.now() },
    }),

  updateWhiteListPrecalc: (): Promise<AxiosResponse<any>> =>
    client.post(
      "/white_list_checker_precalc",
      {},
      {
        ...authConfig(),
        timeout: 180_000,
      },
    ),

  getDnsCheckScriptResult: (id: string | number): Promise<AxiosResponse<any>> =>
    client.get(`/dnscheckscriptresult/${id}/`, authConfig()),

  downloadWhiteListCsvArchive: (): Promise<AxiosResponse<Blob>> =>
    client.get("/white_list_checker_csv_archive", {
      ...authConfig(),
      responseType: "blob",
      timeout: 120_000,
    }),

  listWhiteListUploadTokens: (): Promise<AxiosResponse<any>> =>
    client.get("/white_list_checker_upload_tokens", authConfig()),

  issueWhiteListUploadToken: (
    payload: Record<string, any> = {},
  ): Promise<AxiosResponse<any>> =>
    client.post("/white_list_checker_upload_tokens", payload, authConfig()),

  revokeWhiteListUploadToken: (
    id: string | number,
  ): Promise<AxiosResponse<any>> =>
    client.post(
      `/white_list_checker_upload_tokens/${id}/revoke`,
      {},
      authConfig(),
    ),

  getReportData: (): Promise<AxiosResponse<ReportDataResponseType>> =>
    client.get("/data/"),

  submitReport: (
    payload: any,
  ): Promise<AxiosResponse<SubmitReportResponseType>> =>
    client.post("/report/", payload, webConfig()),

  postStats: (url: string, payload: any): Promise<AxiosResponse<any>> =>
    axios.post(url, payload),
};

export const getDnsMonitoringData = monitorApi.getDnsMonitoringData;
export const checkDnsByDomain = monitorApi.checkDnsByDomain;

export default monitorApi;
